/**
 * search-results-table.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { Subscription } from "rxjs/Rx";

import { Algorithm } from "../algorithms/structure/algorithm";
import { AlgorithmService } from "../algorithms/management/algorithm.service";
import { AlertService } from "../common/alert.service";
import { Subject } from "../subjects/subject";
import {
  Result,
  ResultColumn,
  SearchService
} from "./search.service";

/**
 * The {@link SearchResultsTableComponent} presents the results currently cached
 * by the {@link SearchService} in a tabular format, and provides interactions
 * to open each record or clear the results array.
 */
@Component({
  selector: "search-results-table",
  template: `
    <h3 *ngIf="showTitle">
      Search Results
    </h3>
    <p *ngIf="!search.running && search.resultTime">
      The query found
      {{search.resultCountLabel}}.
      The last request
      <span *ngIf="search.executionSeconds > instantThreshold">
        took
        <span [class.text-danger]="search.executionSeconds > 1">
          {{search.executionSeconds}}
        </span>
        seconds
      </span>
      <span *ngIf="search.executionSeconds < instantThreshold">
        was instantaneous
      </span>
      and finished at
      {{search.resultTime | date:"mediumTime"}}.
    </p>
    <!--<em *ngIf="search.lastResultOpened !== null">
      The last result you opened is highlighted. (It may be on another page.)
    </em>-->
    <table *ngIf="!search.running" class="table table-striped table-responsive">
      <thead *ngIf="search.results?.length > 0">
        <tr>
          <!--<th>Record ID</th>-->
          <th
            *ngFor="let column of search.results[0]?.columns; let i = index"
            [sorts]="search.results"
            [dot]="'columns.' + i"
            class="capitalized">
            {{column.name}}
          </th>
          <th>
            <button
              class="btn btn-default"
              (click)="clearResults()">
              Clear results
            </button>
          </th>
        </tr>
      </thead>
      <tbody *ngIf="search.results">
        <tr
          *ngFor="let result of (search.results | paginate)"
          [class.bg-warning]="result === search.lastResultOpened">
          <!--<th>{{result.recordId}}</th>-->
          <td *ngFor="let data of result.columns">
            <typed-display [input]="data"></typed-display>
          </td>
          <td>
            <!-- link needs generalization if page used for other searches -->
            <button
              *ngIf="result.recordId === null"
              class="btn btn-primary form-control"
              disabled
              title="You do not have permission to view this data"
              (click)="alert('You do not have permission to view this data')">
              Open
            </button>
            <a
              *ngIf="result.recordId !== null && asSelector"
              class="btn btn-primary form-control"
              (click)="open(result)">
              Open
			</a>
			<div
				#openOptions
				*ngIf="result.recordId !== null && !asSelector"
				class="btn-group"
				[class.open]="openOptions.open"
				(mouseleave)="openOptions.open = false">
				<a 
					class="btn btn-primary"
					(click)="openDefault(result)">
					<span class="hidden-sm hidden-xs">Open</span>
					<span
					class="hidden-md hidden-lg glyphicon glyphicon-folder-open">
					</span>
				</a>
				<button
					type="button"
					class="btn btn-primary dropdown-toggle"
					data-toggle="dropdown"
					aria-haspopup="true"
					[attr.aria-expanded]="!!openOptions.open"
					(click)="openOptions.open = !openOptions.open">
					<span class="caret"></span>
				</button>
        <ul class="dropdown-menu right-edge">
          <li>
            <a (click)="open(result, null)">
              Summary Page
            </a>
          </li>
          <li role="separator" class="divider"></li>
					<li *ngFor="let alg of algorithms.bySCType(search.resultContextType)">
						<a (click)="open(result, alg)">
							{{alg.displayName}}
						</a>
					</li>
				</ul>
			</div>
          </td>
        </tr>
        <tr *ngIf="search.moreResultsAvailable && (search.results | onPage : -1)">
          <td colspan="100" class="text-center">
            <button
              class="btn btn-default"
              (click)="search.requestMoreResults()">
              Request more results...
            </button>
        </tr>
      </tbody>
      <caption
        *ngIf="search.results && search.results.length === 0">
        No results
      </caption>
      <caption *ngIf="!search.results">
        You haven't searched yet.
      </caption>
    </table>
    <spinner *ngIf="search.running"></spinner>
    <pager
      *ngIf="search.results?.length"
      [array]="search.results"
      [jumpButtons]="true">
    </pager>
`,
  styles: [
    `caption {
      caption-side: bottom;
      text-align: center;
      font-size: 2em;
      font-style: italic;
      padding: 0.5em;
    }
    table.table>tbody>tr>td {
      vertical-align: middle;
    }`
  ]
})
export class SearchResultsTableComponent
{
  public readonly instantThreshold : number = 0.05;

  /**
   * Whether to display the component in "selector mode", where its function is
   * to *select* a subject and implied to be embedded in a UI for such selection
   * (at present, solely the {@link SubjectSelectorModalComponent}).
   */
  @Input()
  public asSelector : boolean = false;

  /**
   * Whether to display the "search results" headline, which may be unnecessary
   * when the content is embedded elsewhere.
   */
  @Input()
  public showTitle : boolean = true;

  /**
   * Output interface that pings the Subjects that a user selects. Note that
   * this output emits even when the component isn't in selector mode (and is
   * necessary to trigger the navigation action of the "traditional" mode).
   */
  @Output()
  public selection : EventEmitter<Subject> = new EventEmitter();

  /**
   * Construct a new {@link SearchResultsTableComponent}.
   */
  public constructor (
    public search : SearchService,
	public alert : AlertService,
	public algorithms : AlgorithmService
  ) {}

  /**
   * Clear the current search results.
   */
  public clearResults () : void
  {
    this.search.clearResults();
  }

  /**
   * Respond to a user request to open a subject in the results (by emitting
   * the selection to the parent component).
   * @param result The search result selected
   */
  public open (result : Result, target? : Algorithm) : void
  {
    this.search.open(result, !this.asSelector, target).subscribe(
      (subject : Subject) => this.selection.emit(subject));
  }

  public openDefault (result : Result) : void
  {
    const target : Algorithm | null =
      this.algorithms.defaultForOpen(this.search.resultContextType);
    this.open(result, target);
  }

}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/search/search-results-table.component.ts